import Button from '@mui/material/Button'
import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Grid, Link, TextField, useTheme } from '@mui/material'
import { useNavigate, useSearchParams } from 'react-router-dom'
import './home.css'
import styles from './styles'
import {
  logout,
  updateAuthToken,
  updateEmailAddress
} from '../../redux/actions/userActions'
import { getEmailAddress } from '../../utils/jwtUtils'
import { RootState } from '../../redux/reducers'
import { routePaths } from '../../constants'
import { useClientApiContext } from '../context/ClientApiContextProvider'

const TOO_MANY_CLIENTS_MESSAGE =
  'This link has been viewed by too many devices or browsers. Please try again later.'

const Home = () => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const token: string | null = searchParams.get('token')
  const { user } = useSelector((state: RootState) => state)
  const [emailAddress, setEmailAddress] = useState(user.email)
  const [emailError, setEmailError] = useState('')
  const [magicLinkError, setMagicLinkError] = useState(false)
  const [magicLinkSuccess, setMagicLinkSuccess] = useState(false)
  const [linkExpired, setLinkExpired] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')
  const { palette } = useTheme()
  const imageClass = `${palette.mode}-login-logo`
  const useClientContext = useClientApiContext()

  useEffect(() => {
    if (linkExpired) {
      // Create uuid and store it if it is not existed
      dispatch(logout())
    }
  }, [dispatch, linkExpired])

  useEffect(() => {
    if (token && user.id) {
      useClientContext.client
        .loginWithToken(
          { tokenType: 'magic' },
          { token, clientId: user.id, product: 'library' }
        )
        .then(
          (result) => {
            dispatch(updateAuthToken(result.data))
            navigate(routePaths.LIBRARY)
          },
          (error: any) => {
            if (error.response?.data?.errorCode === 'TOO_MANY_CLIENTS') {
              setErrorMessage(TOO_MANY_CLIENTS_MESSAGE)
            } else {
              setErrorMessage('Looks like you clicked an old magic link.')
            }
            setLinkExpired(true)
          }
        )
    }
  }, [dispatch, navigate, token, user.id, useClientContext.client])

  useEffect(() => {
    if (user.auth.accessToken) {
      navigate(routePaths.LIBRARY)
      const emailTrim = getEmailAddress(user.auth.accessToken).trim()
      setEmailAddress(emailTrim)
      dispatch(updateEmailAddress(emailTrim))
    }
  }, [dispatch, navigate, user.auth.accessToken])

  const getMagicLink = (email: string) => {
    setMagicLinkError(false)
    setMagicLinkSuccess(false)
    useClientContext.client
      .requestMagicLink(null, { email, product: 'library' })
      .then(
        (result) => {
          setMagicLinkError(false)
          setMagicLinkSuccess(true)
        },
        (err) => {
          setMagicLinkError(true)
          dispatch(logout())
        }
      )
  }

  const handleSubmit = () => {
    if (!emailAddress) {
      setEmailError('Please enter a valid email address')
      return
    }
    setEmailError('')
    const emailTrim = emailAddress.trim()
    setEmailAddress(emailTrim)
    dispatch(updateEmailAddress(emailTrim))
    getMagicLink(emailTrim)
  }

  const onTextChange = (value: string) => {
    setEmailAddress(value)
    if (value) setEmailError('')
  }

  const getNewLink = () => {
    setLinkExpired(false)
    navigate(routePaths.HOME)
  }

  return (
    <Grid
      container
      spacing={0}
      direction="column"
      alignItems="center"
      justifyContent="center"
      style={{ minHeight: '100vh', padding: 25 }}
    >
      <div className={imageClass}></div>
      {linkExpired ? (
        <>
          <div>{errorMessage}</div>
          {errorMessage !== TOO_MANY_CLIENTS_MESSAGE && (
            <Link onClick={getNewLink} style={styles.link}>
              Get a new magic link here
            </Link>
          )}
        </>
      ) : token ? (
        <div>Loading...</div>
      ) : (
        <Grid
          item
          flexDirection={'column'}
          display={'flex'}
          alignItems={'center'}
          height={'100%'}
          className={'home-container'}
        >
          <h2>Sign into your digital library</h2>
          <TextField
            sx={{ minWidth: 300 }}
            onChange={(e) => onTextChange(e.target.value)}
            value={emailAddress}
            label={'Email Address'} //optional
            style={styles.textField}
            {...(emailError && {
              error: true,
              helperText: emailError
            })}
            onKeyDown={(ev) => {
              if (ev.key === 'Enter') {
                // Do code here
                ev.preventDefault()
                handleSubmit()
              }
            }}
          />
          <Button variant="contained" type="submit" onClick={handleSubmit}>
            Log In
          </Button>
          {magicLinkError && (
            <span className="login-error">
              Sorry, we couldn't find a subscriber with that email.
            </span>
          )}
          {magicLinkSuccess && (
            <span className="login-success">
              We've emailed you a magic sign-in link! Click the link in that
              email to log in.
            </span>
          )}
        </Grid>
      )}
    </Grid>
  )
}

export default Home
